/** /settings/ai/features — operator-editable prompt + LLM call
 *  settings, one row per feature. Foundation page; polish (preview,
 *  validation, per-feature model picker UI, JSON Schema editor) is
 *  queued as follow-up.
 *
 *  Each feature shows:
 *    - Catalog default (read-only fallback if no config row exists)
 *    - System prompt textarea
 *    - think toggle (null / true / false)
 *    - Temperature number input (0-2, blank = use llm.ts default)
 *    - Max tokens number input
 *    - Save button
 *
 *  On save, the row is upserted in feature_configs; the next routeLlmCall
 *  for that feature picks up the new values via the in-process cache
 *  (60 s TTL, invalidated on write).
 *
 *  This page intentionally does NOT let the operator pick provider+
 *  model — that lives in /settings/ai/bindings (per-user, per-
 *  feature). feature_configs is the system-wide prompt and LLM-call
 *  layer; bindings is the user's provider override. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Save, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { getCsrfToken } from "../lib/auth";
import { TeachingBanner } from "../components/TeachingBanner";

type CatalogDefault = {
  provider: string;
  model: string;
  description?: string;
};

type FeatureConfigRow = {
  featureId: string;
  systemPrompt: string | null;
  promptTemplate: string | null;
  modelPreference: string | null;
  fallbackChain: string[] | null;
  think: boolean | null;
  temperature: number | null;
  maxTokens: number | null;
  scope: "private" | "public" | "either";
  expectedOutputSchema: string | null;
  userCustomInstructions: string | null;
  updatedAt: string;
  updatedBy: string | null;
};

type FeatureConfigEntry = {
  featureId: string;
  name: string;
  category: string;
  catalogDefault: CatalogDefault;
  config: FeatureConfigRow | null;
};

type DraftState = {
  systemPrompt: string;
  think: "" | "true" | "false";
  temperature: string;
  maxTokens: string;
  modelPreference: string;
  userCustomInstructions: string;
};

/** Curated list of canonical model ids the operator can pick from
 *  the per-feature model dropdown. Mirrors PROVIDER_META in Settings.tsx;
 *  keep in sync when adding a model. The blank string maps to "follow
 *  the FEATURE_CATALOG default + per-user binding" — that's the
 *  conservative choice and the default offered to the operator. */
const MODEL_CHOICES: { value: string; label: string }[] = [
  { value: "", label: "(use catalog default + user binding)" },
  { value: "local:qwen3:4b", label: "Local: qwen3:4b (fast, 4B params)" },
  { value: "local:qwen3:8b", label: "Local: qwen3:8b (default, 8B params)" },
  { value: "local:qwen3:14b", label: "Local: qwen3:14b (slow, 14B params)" },
  { value: "local:gemma3:4b", label: "Local: gemma3:4b" },
  { value: "local:gemma3:12b", label: "Local: gemma3:12b" },
  { value: "api:gemini-2.5-flash", label: "Cloud: Gemini 2.5 Flash" },
  { value: "api:gemini-2.5-pro", label: "Cloud: Gemini 2.5 Pro" },
  { value: "api:claude-sonnet-4-6", label: "Cloud: Claude Sonnet 4.6" },
  { value: "api:claude-opus-4-7", label: "Cloud: Claude Opus 4.7" },
  { value: "api:claude-haiku-4-5-20251001", label: "Cloud: Claude Haiku 4.5" },
];

type PreviewResult = {
  text: string;
  binding: { provider: string; model: string };
  usage: { inputTokens: number; outputTokens: number; latencyMs: number };
};

function configToDraft(c: FeatureConfigRow | null): DraftState {
  return {
    systemPrompt: c?.systemPrompt ?? "",
    think: c?.think == null ? "" : c.think ? "true" : "false",
    temperature: c?.temperature == null ? "" : String(c.temperature),
    maxTokens: c?.maxTokens == null ? "" : String(c.maxTokens),
    modelPreference: c?.modelPreference ?? "",
    userCustomInstructions: c?.userCustomInstructions ?? "",
  };
}

export function AiFeaturesSettings() {
  const [entries, setEntries] = useState<FeatureConfigEntry[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, DraftState>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewInput, setPreviewInput] = useState<Record<string, string>>({});
  const [previewing, setPreviewing] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<Record<string, PreviewResult | null>>({});
  const [previewErr, setPreviewErr] = useState<Record<string, string | null>>({});

  async function load() {
    setLoading(true);
    try {
      const res = await fetch("/api/settings/ai/feature-configs", {
        credentials: "same-origin",
        cache: "no-store",
      });
      if (!res.ok) {
        setErr(`/api/settings/ai/feature-configs returned ${res.status}`);
        return;
      }
      const body = (await res.json()) as { ok: boolean; configs: FeatureConfigEntry[] };
      setEntries(body.configs);
      const next: Record<string, DraftState> = {};
      for (const e of body.configs) next[e.featureId] = configToDraft(e.config);
      setDraft(next);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function preview(featureId: string) {
    const sample = (previewInput[featureId] ?? "").trim();
    if (!sample) {
      setPreviewErr({ ...previewErr, [featureId]: "Enter a sample prompt first." });
      return;
    }
    setPreviewing(featureId);
    setPreviewErr({ ...previewErr, [featureId]: null });
    setPreviewResult({ ...previewResult, [featureId]: null });
    try {
      const res = await fetch(`/api/settings/ai/feature-configs/${encodeURIComponent(featureId)}/preview`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCsrfToken() ?? "" },
        body: JSON.stringify({ sampleInput: sample }),
      });
      const j = (await res.json()) as { ok: boolean; text?: string; binding?: PreviewResult["binding"]; usage?: PreviewResult["usage"]; error?: string };
      if (!res.ok || !j.ok || !j.text || !j.binding || !j.usage) {
        setPreviewErr({ ...previewErr, [featureId]: j.error ?? `preview failed: ${res.status}` });
        return;
      }
      setPreviewResult({ ...previewResult, [featureId]: { text: j.text, binding: j.binding, usage: j.usage } });
    } catch (e) {
      setPreviewErr({ ...previewErr, [featureId]: e instanceof Error ? e.message : String(e) });
    } finally {
      setPreviewing(null);
    }
  }

  async function save(featureId: string) {
    setSaving(featureId);
    setErr(null);
    const d = draft[featureId];
    const body: Record<string, unknown> = {
      systemPrompt: d.systemPrompt.trim() === "" ? null : d.systemPrompt,
      think: d.think === "" ? null : d.think === "true",
      temperature: d.temperature.trim() === "" ? null : Number(d.temperature),
      maxTokens: d.maxTokens.trim() === "" ? null : Number(d.maxTokens),
      modelPreference: d.modelPreference.trim() === "" ? null : d.modelPreference,
      userCustomInstructions: d.userCustomInstructions.trim() === "" ? null : d.userCustomInstructions,
    };
    try {
      const res = await fetch(`/api/settings/ai/feature-configs/${encodeURIComponent(featureId)}`, {
        method: "PUT",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCsrfToken() ?? "" },
        body: JSON.stringify(body),
      });
      const j = (await res.json()) as { ok: boolean; error?: string };
      if (!res.ok || !j.ok) {
        setErr(j.error ?? `save failed: ${res.status}`);
        return;
      }
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(null);
    }
  }

  const grouped = entries.reduce<Record<string, FeatureConfigEntry[]>>((acc, e) => {
    (acc[e.category] ??= []).push(e);
    return acc;
  }, {});

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 text-slate-900 dark:text-slate-100">
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">AI feature settings</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Edit the prompt and LLM call settings for each Memso feature. Per-user provider /
            model overrides live in <Link to="/settings/ai/bindings" className="underline">bindings</Link>.
          </p>
          <p className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-snug text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
            <strong className="text-slate-700 dark:text-slate-300">Model picker tip.</strong>{" "}
            Mail-family features default to <code>local:qwen3:14b</code> for interactive use
            (bench mean 2.6 s, p95 4.3 s; better calibration on routine vs urgent than 8b).
            Bulk paths (backfills, the connector sweep, the bench harness) should pass
            <code> model: "local:qwen3:8b"</code> explicitly via <code>llmComplete()</code>{" "}
            for 1.8x throughput.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Reload
        </button>
      </header>

      <div className="mb-4">
        <TeachingBanner id="settings_ai_features_overview" />
      </div>

      {err && (
        <div className="mb-4 rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100">
          {err}
        </div>
      )}

      {loading && entries.length === 0 && (
        <div className="rounded-md border border-slate-200 bg-white p-6 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900">
          Loading feature catalog…
        </div>
      )}

      {Object.entries(grouped).map(([category, items]) => (
        <section key={category} className="mb-6">
          <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {category}
          </h2>
          <ul className="divide-y divide-slate-200 overflow-hidden rounded-lg border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
            {items.map((e) => {
              const open = openId === e.featureId;
              const d = draft[e.featureId];
              const isSaving = saving === e.featureId;
              return (
                <li key={e.featureId}>
                  <button
                    type="button"
                    onClick={() => setOpenId(open ? null : e.featureId)}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    {open ? <ChevronDown className="mt-0.5 h-4 w-4 flex-none" /> : <ChevronRight className="mt-0.5 h-4 w-4 flex-none" />}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-sm font-medium">{e.name}</span>
                        <code className="flex-none text-[10px] text-slate-500">{e.featureId}</code>
                      </div>
                      <div className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                        default · {e.catalogDefault.provider}/{e.catalogDefault.model}
                        {e.config && (
                          <span className="ml-2 inline-flex items-center rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                            configured
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                  {open && d && (
                    <div className="space-y-3 border-t border-slate-200 bg-slate-50/60 px-4 py-3 text-sm dark:border-slate-800 dark:bg-slate-950/40">
                      {/* Canonical rubric — read-only. Backfilled from
                          lib/canonical-prompts.ts on every boot, so the
                          system contract can't drift. Operators tune
                          behaviour via the "Your custom instructions"
                          textarea below; the router composes them at
                          call time with a labelled separator. */}
                      <div>
                        <div className="mb-1 flex items-baseline justify-between">
                          <span className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                            Canonical rubric{" "}
                            <span className="text-[10px] font-normal text-slate-500">read-only · system contract</span>
                          </span>
                        </div>
                        <details className="rounded border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                          <summary className="cursor-pointer px-2 py-1.5 text-[11px] text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800">
                            {d.systemPrompt
                              ? `${d.systemPrompt.length} chars — click to expand`
                              : "(not configured)"}
                          </summary>
                          <pre className="max-h-96 overflow-y-auto whitespace-pre-wrap break-words border-t border-slate-200 px-2 py-1.5 font-mono text-[11px] leading-snug text-slate-700 dark:border-slate-700 dark:text-slate-300">{d.systemPrompt || "(empty)"}</pre>
                        </details>
                      </div>

                      {/* Editable custom instructions — operator's
                          layer. Composed under a "USER PRIORITY
                          OVERRIDES" header so the model knows these
                          rules win over the canonical defaults. */}
                      <label className="block">
                        <span className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                          Your custom instructions
                        </span>
                        <span className="mb-1 mt-0.5 block text-[10px] leading-snug text-slate-500 dark:text-slate-400">
                          Optional. Rules that customize classification for your context. One per line.
                          Examples:{" "}
                          <span className="font-mono text-[10px]">
                            Emails from HR about benefits → P2 FYI · Anything from VP Engineering → at least P4 · Out-of-office auto-replies → P1 Noise
                          </span>
                        </span>
                        <textarea
                          value={d.userCustomInstructions}
                          onChange={(ev) => setDraft({ ...draft, [e.featureId]: { ...d, userCustomInstructions: ev.target.value } })}
                          rows={5}
                          placeholder="(empty — only the canonical rubric applies)"
                          className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 font-mono text-xs leading-snug dark:border-slate-700 dark:bg-slate-900"
                        />
                      </label>
                      <label className="block">
                        <span className="block text-xs font-medium text-slate-700 dark:text-slate-300">Model</span>
                        <select
                          value={d.modelPreference}
                          onChange={(ev) => setDraft({ ...draft, [e.featureId]: { ...d, modelPreference: ev.target.value } })}
                          className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-900"
                        >
                          {MODEL_CHOICES.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        <label className="block">
                          <span className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                            Think mode
                          </span>
                          <select
                            value={d.think}
                            onChange={(ev) => setDraft({ ...draft, [e.featureId]: { ...d, think: ev.target.value as DraftState["think"] } })}
                            className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-900"
                          >
                            <option value="">default (model decides)</option>
                            <option value="true">on (qwen3 reasoning mode)</option>
                            <option value="false">off (short-form, no reasoning)</option>
                          </select>
                          <span className="mt-1 block text-[10px] leading-snug text-slate-500 dark:text-slate-400">
                            qwen3:8b ON was 280 ms faster mean / 440 ms faster p95 than OFF
                            (bench 2026-05-10). qwen3:14b returns empty <code>{`{}`}</code> when
                            ON is combined with JSON output mode, so leave OFF on 14b. Cloud
                            providers ignore this flag.
                          </span>
                        </label>
                        <label className="block">
                          <span className="block text-xs font-medium text-slate-700 dark:text-slate-300">Temperature</span>
                          <input
                            type="number"
                            min={0}
                            max={2}
                            step={0.05}
                            value={d.temperature}
                            onChange={(ev) => setDraft({ ...draft, [e.featureId]: { ...d, temperature: ev.target.value } })}
                            placeholder="0"
                            className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-900"
                          />
                        </label>
                        <label className="block">
                          <span className="block text-xs font-medium text-slate-700 dark:text-slate-300">Max tokens</span>
                          <input
                            type="number"
                            min={1}
                            max={32000}
                            step={1}
                            value={d.maxTokens}
                            onChange={(ev) => setDraft({ ...draft, [e.featureId]: { ...d, maxTokens: ev.target.value } })}
                            placeholder="1024"
                            className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-900"
                          />
                        </label>
                      </div>
                      {/* Preview: dispatches the actual router with the
                          current saved settings so the operator can see
                          what callers will see. Note: it runs the
                          PERSISTED config, not the current unsaved draft
                          fields — Save first if you want to test edits. */}
                      <div className="space-y-2 rounded-md border border-dashed border-slate-300 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900/40">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium text-slate-700 dark:text-slate-300">Preview against the live router</span>
                          <button
                            type="button"
                            onClick={() => void preview(e.featureId)}
                            disabled={previewing === e.featureId}
                            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-slate-50 px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                          >
                            {previewing === e.featureId ? "Running…" : "Run preview"}
                          </button>
                        </div>
                        <textarea
                          value={previewInput[e.featureId] ?? ""}
                          onChange={(ev) => setPreviewInput({ ...previewInput, [e.featureId]: ev.target.value })}
                          rows={3}
                          placeholder="Sample input. Will be sent through routeLlmCall with this feature's saved config."
                          className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 font-mono text-[11px] leading-snug dark:border-slate-700 dark:bg-slate-950"
                        />
                        {previewErr[e.featureId] && (
                          <div className="text-[11px] text-rose-700 dark:text-rose-300">{previewErr[e.featureId]}</div>
                        )}
                        {previewResult[e.featureId] && (
                          <div className="rounded bg-slate-100 px-2 py-1.5 dark:bg-slate-800">
                            <div className="mb-1 text-[10px] text-slate-500 dark:text-slate-400">
                              {previewResult[e.featureId]!.binding.provider}/{previewResult[e.featureId]!.binding.model}
                              {" · "}
                              in={previewResult[e.featureId]!.usage.inputTokens} out={previewResult[e.featureId]!.usage.outputTokens}
                              {" · "}
                              {previewResult[e.featureId]!.usage.latencyMs} ms
                            </div>
                            <pre className="whitespace-pre-wrap font-mono text-[11px] leading-snug text-slate-800 dark:text-slate-200">{previewResult[e.featureId]!.text}</pre>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-[11px] text-slate-500">
                          {e.config
                            ? `updated ${new Date(e.config.updatedAt).toLocaleString()}`
                            : "not yet configured · uses catalog default"}
                        </span>
                        <button
                          type="button"
                          onClick={() => void save(e.featureId)}
                          disabled={isSaving}
                          className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
                        >
                          <Save className="h-3.5 w-3.5" />
                          {isSaving ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      <footer className="mt-8 text-xs text-slate-500 dark:text-slate-400">
        <p>
          Per-feature provider + model overrides go in{" "}
          <Link to="/settings/ai/bindings" className="underline">bindings</Link>. This page is for the
          shared prompt and LLM call parameters.
        </p>
      </footer>
    </div>
  );
}
